import { useState, useEffect } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { getApiBaseUrl } from '../utils/api';

const WholesaleOrderPage = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [lastCreatedOrderReceipt, setLastCreatedOrderReceipt] = useState(null);
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [items, setItems] = useState([
    {
      category: '',
      model: '',
      modelName: '',
      modelNumber: '',
      unitPrice: 0,
      currentStock: 0,
      qty: 1,
      totalValue: 0,
      advance: 0,
      colorRequest: ''
    }
  ]);

  // Fetch inventory dropdown on component mount
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/inventory/dropdown?type=wholesale`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setInventory(data);
        }
      } catch (error) {
        toast.error('Failed to load inventory');
        console.error('Error fetching inventory:', error);
      }
    };
    fetchInventory();
  }, []);

  // Calculate totals
  const grandTotal = items.reduce((sum, item) => sum + item.totalValue, 0);
  const totalAdvance = items.reduce((sum, item) => sum + item.advance, 0);
  const balance = grandTotal - totalAdvance;

  // Handle category selection
  const handleCategoryChange = (index, category) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      category: category,
      model: '', // Reset model selection when category changes
      modelName: '',
      modelNumber: '',
      unitPrice: 0,
      currentStock: 0,
      totalValue: 0,
      advance: 0
    };
    setItems(newItems);
  };

  // Filter inventory by category
  const getFilteredInventory = (category) => {
    if (!category) return [];
    return inventory.filter(item => item.size === category.toLowerCase());
  };

  // Handle model selection
  const handleModelChange = (index, modelId) => {
    const selectedModel = inventory.find(item => item._id === modelId);
    if (selectedModel) {
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        model: modelId,
        modelName: selectedModel.modelName,
        modelNumber: selectedModel.modelNumber,
        unitPrice: selectedModel.price,
        currentStock: selectedModel.currentStock,
        totalValue: selectedModel.price * newItems[index].qty
      };
      setItems(newItems);
    }
  };

  // Handle quantity change (editable for wholesale)
  const handleQtyChange = (index, qty) => {
    const quantity = parseInt(qty) || 0;
    const newItems = [...items];
    newItems[index].qty = quantity;
    newItems[index].totalValue = newItems[index].unitPrice * quantity;
    setItems(newItems);
  };

  // Handle advance change
  const handleAdvanceChange = (index, advance) => {
    const newItems = [...items];
    newItems[index].advance = parseFloat(advance) || 0;
    setItems(newItems);
  };

  // Handle color request change
  const handleColorRequestChange = (index, colorRequest) => {
    const newItems = [...items];
    newItems[index].colorRequest = colorRequest;
    setItems(newItems);
  };

  // Add new item row
  const addItem = () => {
    setItems([...items, {
      category: '',
      model: '',
      modelName: '',
      modelNumber: '',
      unitPrice: 0,
      currentStock: 0,
      qty: 1,
      totalValue: 0,
      advance: 0,
      colorRequest: ''
    }]);
  };

  // Remove item row
  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  // Validate form
  const validateForm = () => {
    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return false;
    }
    if (!phone.trim()) {
      toast.error('Phone number is required');
      return false;
    }
    // Indian mobile validation: 10 digits starting with 6-9
    if (!/^[6-9]\d{9}$/.test(phone.trim())) {
      toast.error('Please enter a valid 10-digit Indian mobile number');
      return false;
    }
    if (!date) {
      toast.error('Date is required');
      return false;
    }

    // Check if all items have categories and models selected
    for (let i = 0; i < items.length; i++) {
      if (!items[i].category) {
        toast.error(`Please select a category for item ${i + 1}`);
        return false;
      }
      if (!items[i].model) {
        toast.error(`Please select a model for item ${i + 1}`);
        return false;
      }

      // Find the inventory item to check availability
      const inventoryItem = inventory.find(inv => inv._id === items[i].model);
      if (inventoryItem && !inventoryItem.isAvailable) {
        toast.error(`Cannot select ${inventoryItem.modelName} - item is out of stock`);
        return false;
      }

      if (items[i].qty <= 0) {
        toast.error(`Quantity must be greater than 0 for item ${i + 1}`);
        return false;
      }
      if (items[i].qty > items[i].currentStock) {
        toast.error(`Insufficient stock for ${items[i].modelName}. Available: ${items[i].currentStock}`);
        return false;
      }
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const orderData = {
        type: 'wholesale',
        customerName: customerName.trim(),
        phone: phone.trim(),
        date,
        items: items.map(item => ({
          model: item.model,
          qty: item.qty,
          advance: item.advance,
          colorRequest: item.colorRequest
        }))
      };

      const response = await fetch(`${getApiBaseUrl()}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const result = await response.json();

        try {
          // Show success notification with order details
          toast.success(`📦 Wholesale Order ${result.order.orderNo} Created Successfully!`, {
            duration: 6000,
            icon: '✅',
            style: {
              background: '#dbeafe',
              color: '#1e40af',
              border: '2px solid #2563eb',
              fontSize: '14px',
              fontWeight: 'bold'
            }
          });

          // Additional notification about PDF download
          // setTimeout(() => {
          //   toast.success('📄 Receipt PDF Downloaded Automatically', {
          //     duration: 4000,
          //     icon: '📋',
          //     style: {
          //       background: '#e0f2fe',
          //       color: '#0c4a6e',
          //       border: '1px solid #0284c7'
          //     }
          //   });
          // }, 1000);

          // Store receipt for download
          if (result.pdfBase64) {
            setLastCreatedOrderReceipt({
              content: result.pdfBase64,
              orderNo: result.order.orderNo,
              isPdf: true
            });
          }

          // Reset form (but keep receipt data for download)
          setCustomerName('');
          setPhone('');
          setDate(new Date().toISOString().split('T')[0]);
          // Keep lastCreatedOrderReceipt for download button
          setItems([{
            category: '',
            model: '',
            modelName: '',
            modelNumber: '',
            unitPrice: 0,
            currentStock: 0,
            qty: 1,
            totalValue: 0,
            advance: 0,
            colorRequest: ''
          }]);
        } catch (successError) {
          console.error('Error in success handling:', successError);
          // Still show success even if there's an error in handling
          toast.success('📦 Wholesale Order Created Successfully!', {
            duration: 6000,
            icon: '✅',
            style: {
              background: '#dbeafe',
              color: '#1e40af',
              border: '2px solid #2563eb',
              fontSize: '14px',
              fontWeight: 'bold'
            }
          });
        }
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create order');
      }
    } catch (error) {
      toast.error('Failed to create order');
      console.error('Error creating order:', error);
    } finally {
      setLoading(false);
    }
  };

  // Download receipt function (PDF or text fallback)
  const downloadReceipt = (content, orderNo) => {
    try {
      // Check if it's PDF (base64) or text
      if (lastCreatedOrderReceipt?.isPdf) {
        // PDF download from base64
        const byteCharacters = atob(content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Receipt_${orderNo}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // Text download (fallback)
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Receipt_${orderNo}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      toast.success('📄 Receipt Downloaded Successfully!', {
        duration: 3000,
        icon: '✅'
      });
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('Failed to download receipt');
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">
              📦 New Wholesale Order
            </h1>
            <p className="text-gray-600">Create a new wholesale order for Ganesh idols</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-xl p-6">
            {/* Customer Details */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Customer Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter customer name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="10-digit mobile number"
                    pattern="[6-9][0-9]{9}"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Order Items</h2>

              {items.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-medium">Item {index + 1}</h3>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category *
                      </label>
                      <select
                        value={item.category}
                        onChange={(e) => handleCategoryChange(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select category</option>
                        <option value="small">Small (1-20)</option>
                        <option value="medium">Medium (21-30)</option>
                        <option value="large">Large (31-40)</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Model *
                      </label>
                      <select
                        value={item.model}
                        onChange={(e) => handleModelChange(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                        disabled={!item.category}
                      >
                        <option value="">
                          {item.category ? 'Select a model' : 'Select category first'}
                        </option>
                        {getFilteredInventory(item.category).map(inv => (
                          <option
                            key={inv._id}
                            value={inv._id}
                            disabled={!inv.isAvailable}
                            className={!inv.isAvailable ? 'text-red-600 bg-red-50' : ''}
                          >
                            {inv.isAvailable ? (
                              `${inv.modelName} - ${inv.modelNumber} (₹${inv.price.toLocaleString('en-IN')}, Stock: ${inv.currentStock})`
                            ) : (
                              `🚫 ${inv.modelName} - ${inv.modelNumber} (₹${inv.price.toLocaleString('en-IN')}) - OUT OF STOCK`
                            )}
                          </option>
                        ))}
                      </select>

                      {/* Model Photo Display */}
                      {item.model && (() => {
                        const selectedModel = inventory.find(inv => inv._id === item.model);
                        return selectedModel?.photo ? (
                          <div className="mt-3 p-3 bg-white rounded-lg border">
                            <div className="flex items-center gap-3">
                              <img
                                src={selectedModel.photo}
                                alt={`${selectedModel.modelName} preview`}
                                className="w-12 h-12 object-cover rounded border shadow-sm"
                              />
                              <div>
                                <p className="text-xs font-medium text-gray-800">
                                  {selectedModel.modelName}
                                </p>
                                <p className="text-xs text-gray-600">
                                  Model {selectedModel.modelNumber}
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity *
                      </label>
                      <input
                        type="number"
                        value={item.qty}
                        onChange={(e) => handleQtyChange(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                        required
                      />
                      {item.currentStock > 0 && (
                        <p className="text-xs text-gray-500 mt-1">Available: {item.currentStock}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit Price
                      </label>
                      <input
                        type="text"
                        value={`₹${item.unitPrice.toLocaleString('en-IN')}`}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Value
                      </label>
                      <input
                        type="text"
                        value={`₹${item.totalValue.toLocaleString('en-IN')}`}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 font-semibold"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Advance Payment
                      </label>
                      <input
                        type="number"
                        value={item.advance}
                        onChange={(e) => handleAdvanceChange(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Color Request
                      </label>
                      <textarea
                        value={item.colorRequest}
                        onChange={(e) => handleColorRequestChange(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="2"
                        placeholder="Any special color requirements..."
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addItem}
                className="w-full py-2 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              >
                + Add Another Item
              </button>
            </div>

            {/* Summary */}
            <div className="mb-6 bg-blue-50 p-4 rounded-lg">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Order Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Grand Total</p>
                  <p className="text-2xl font-bold text-blue-600">₹{grandTotal.toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Total Advance</p>
                  <p className="text-2xl font-bold text-green-600">₹{totalAdvance.toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Balance Due</p>
                  <p className="text-2xl font-bold text-red-600">₹{balance.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </div>

            {/* Submit Button and Download Link */}
            <div className="flex justify-center items-center gap-4">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Creating Order...' : 'Create Wholesale Order'}
              </button>

              {lastCreatedOrderReceipt && (
                <button
                  type="button"
                  onClick={() => downloadReceipt(lastCreatedOrderReceipt.content, lastCreatedOrderReceipt.orderNo)}
                  className="px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 animate-pulse"
                >
                  📄 Download {lastCreatedOrderReceipt.isPdf ? 'PDF' : 'Text'} Receipt
                </button>
              )}
            </div>
          </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default WholesaleOrderPage;
